import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { sendTestEmail } from '../../services/emailService';
import SafeIcon from '../../common/SafeIcon';

const KNOWN_ADMINS = [
  { email: 'admin@dayclap.com', name: 'Admin' }
];

const Settings = () => {
  const [settings, setSettings] = useState({
    appName: 'DayClap', senderEmail: 'noreply@app.dayclap.com', senderName: 'DayClap',
    enableWelcomeEmail: true, enableInvitationEmail: true, enableResponseEmail: true,
    maintenanceMode: false
  });
  const [saved, setSaved] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState(null);
  const [loadingAdmins, setLoadingAdmins] = useState(true);

  useEffect(() => { loadAdmins(); }, []);

  const loadAdmins = async () => {
    try {
      // Step 1: Try querying user_profiles with role='admin'
      const { data: adminProfiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, name, email, avatar_url, created_at, is_blocked, role')
        .eq('role', 'admin')
        .order('created_at', { ascending: false });

      if (!profileError && adminProfiles && adminProfiles.length > 0) {
        setAdmins(adminProfiles);
        return;
      }

      // Step 2: Fallback — query by known admin emails
      const fallbackAdmins = [];
      for (const known of KNOWN_ADMINS) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('id, name, email, avatar_url, created_at, is_blocked, role')
          .eq('email', known.email)
          .maybeSingle();

        if (profile) {
          fallbackAdmins.push(profile);
        }
      }

      if (fallbackAdmins.length > 0) {
        setAdmins(fallbackAdmins);
        return;
      }

      // Step 3: Final fallback — show known admins with pending status
      setAdmins(KNOWN_ADMINS.map(a => ({
        id: 'pending',
        name: a.name,
        email: a.email,
        avatar_url: '',
        created_at: new Date().toISOString(),
        is_blocked: false,
        role: 'admin',
        _pending: true
      })));
    } catch (err) {
      console.error('Error loading admins:', err);
      setAdmins(KNOWN_ADMINS.map(a => ({
        id: 'pending', name: a.name, email: a.email,
        avatar_url: '', created_at: new Date().toISOString(),
        is_blocked: false, role: 'admin', _pending: true
      })));
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    setTestStatus({ type: 'sending', message: 'Sending test email...' });
    try {
      const result = await sendTestEmail(testEmail);
      if (result.success) {
        setTestStatus({ type: 'success', message: `Test email sent to ${testEmail}! Check your inbox.` });
      } else {
        setTestStatus({ type: 'error', message: result.error || 'Failed to send test email' });
      }
    } catch (err) {
      setTestStatus({ type: 'error', message: err.message || 'Unexpected error' });
    }
    setTimeout(() => setTestStatus(null), 8000);
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-gray-500 mt-1">Configure DayClap application settings</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* General */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <SafeIcon name="Settings" className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800">General Settings</h3>
              <p className="text-xs text-gray-400">Basic application configuration</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Application Name</label>
            <input
              type="text" value={settings.appName}
              onChange={(e) => setSettings({ ...settings, appName: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none"
            />
          </div>
        </div>

        {/* Email Configuration */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
              <SafeIcon name="Mail" className="text-pink-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800">Email Configuration</h3>
              <p className="text-xs text-gray-400">Sender identity and notifications</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Sender Name</label>
                <input
                  type="text" value={settings.senderName}
                  onChange={(e) => setSettings({ ...settings, senderName: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Sender Email</label>
                <input
                  type="email" value={settings.senderEmail}
                  onChange={(e) => setSettings({ ...settings, senderEmail: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none"
                />
              </div>
            </div>
            <div className="space-y-3 pt-4 border-t border-gray-100">
              {[
                { key: 'enableWelcomeEmail', label: 'Send welcome email to new users' },
                { key: 'enableInvitationEmail', label: 'Send email notifications for invitations' },
                { key: 'enableResponseEmail', label: 'Send email notifications for responses' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox" checked={settings[key]}
                    onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Email Testing */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <SafeIcon name="Send" className="text-emerald-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800">Email Testing</h3>
              <p className="text-xs text-gray-400">Verify email sending is working</p>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Send a test email to verify that your email service is configured correctly and emails are being delivered.
          </p>
          <div className="space-y-3">
            <div className="relative">
              <SafeIcon name="Mail" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address to test..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
              />
            </div>
            <button
              onClick={handleSendTest}
              disabled={!testEmail || testStatus?.type === 'sending'}
              className="w-full bg-emerald-600 text-white font-semibold py-3 rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              {testStatus?.type === 'sending' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <SafeIcon name="Send" className="text-sm" />
                  Send Test Email
                </>
              )}
            </button>
            {testStatus && testStatus.type !== 'sending' && (
              <div className={`p-3 rounded-xl text-sm flex items-center gap-2 ${
                testStatus.type === 'success'
                  ? 'bg-green-50 border border-green-100 text-green-700'
                  : 'bg-red-50 border border-red-100 text-red-700'
              }`}>
                <SafeIcon name={testStatus.type === 'success' ? 'CheckCircle' : 'AlertCircle'} className="flex-shrink-0" />
                {testStatus.message}
              </div>
            )}
          </div>
        </div>

        {/* Email Service Info */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <SafeIcon name="Info" className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800">Email Service Info</h3>
              <p className="text-xs text-gray-400">How DayClap sends emails</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <SafeIcon name="CheckCircle" className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Emailit API (Active)</p>
                  <p className="text-xs text-blue-600 mt-1">
                    DayClap uses the <strong>Emailit API</strong> via a Supabase Edge Function to send transactional emails. This is already configured and working.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <SafeIcon name="Info" className="text-gray-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-700">Do I need SMTP settings?</p>
                  <p className="text-xs text-gray-500 mt-1">
                    <strong>No.</strong> You do not need to add SMTP settings in Supabase. DayClap uses the Emailit API (a REST-based email service), not SMTP. The edge function handles all email delivery.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <SafeIcon name="Server" className="text-gray-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-700">Supabase Built-in Auth Emails</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supabase has its own built-in email system (for auth confirmations, password resets). This uses Supabase's default SMTP. To customize these, go to <strong>Supabase Dashboard → Authentication → Email Templates</strong>. Use the templates from the Template Library tab — they use Supabase's native variables like <code className="bg-gray-200 px-1 rounded">{'{{ .ConfirmationURL }}'}</code> and <code className="bg-gray-200 px-1 rounded">{'{{ .Token }}'}</code>.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <SafeIcon name="AlertTriangle" className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Important Note</p>
                  <p className="text-xs text-amber-600 mt-1">
                    Password reset and email confirmation emails are sent by <strong>Supabase Auth</strong> (not Emailit). Paste the HTML from the Template Library into Supabase Dashboard → Authentication → Email Templates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Accounts */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 xl:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <SafeIcon name="Shield" className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800">Admin Accounts</h3>
              <p className="text-xs text-gray-400">{admins.length} administrator{admins.length !== 1 ? 's' : ''} with platform access</p>
            </div>
          </div>

          {loadingAdmins ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-8">
              <SafeIcon name="Shield" className="text-3xl text-gray-300 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No admin accounts found</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-100">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Admin</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {admins.map((admin, index) => (
                    <tr key={admin.id || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={admin.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${admin.email}`}
                            alt={admin.name}
                            className="w-10 h-10 rounded-full ring-2 ring-purple-100"
                          />
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">{admin.name || 'Admin'}</p>
                            <p className="text-xs text-gray-400">{admin.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {admin._pending ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-amber-100 text-amber-700">
                            <SafeIcon name="Clock" className="text-xs" />
                            Pending Setup
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-purple-100 text-purple-700">
                            <SafeIcon name="Shield" className="text-xs" />
                            Administrator
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(admin.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {admins.some(a => a._pending) && (
            <div className="mt-4 bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-start gap-3">
              <SafeIcon name="AlertTriangle" className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Admin profile needs setup</p>
                <p className="text-xs text-amber-600 mt-1">
                  Run the migration <strong>1786970000000-ensure-admin-role.sql</strong> in Supabase SQL Editor to set the admin role properly.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* System */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 xl:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <SafeIcon name="AlertTriangle" className="text-red-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800">System Settings</h3>
              <p className="text-xs text-gray-400">Maintenance and safety controls</p>
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox" checked={settings.maintenanceMode}
              onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
              className="w-5 h-5 rounded border-gray-300 text-pink-500 focus:ring-pink-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Maintenance Mode</span>
              <p className="text-xs text-gray-400">Temporarily disable the application for maintenance</p>
            </div>
          </label>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleSave}
          className="bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold px-8 py-3.5 rounded-xl shadow-lg shadow-pink-200 hover:shadow-xl transition-all"
        >
          Save Settings
        </button>
        {saved && (
          <div className="text-green-600 font-semibold flex items-center gap-2 bg-green-50 px-4 py-2 rounded-xl">
            <SafeIcon name="Check" /> Settings saved!
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;